import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { COLORS } from "../../globalvariables";

function AssetsSection({ title, valuesData }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Make sure we have valid data
  const values = Array.isArray(valuesData) ? valuesData : [];
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const sectionElement = document.getElementById("assets");
    if (sectionElement) {
      observer.observe(sectionElement);
    }

    return () => {
      if (sectionElement) {
        observer.unobserve(sectionElement);
      }
    };
  }, []);

  // Early return after hooks are declared
  if (values.length === 0) {
    return (
      <div className="text-center py-10" style={{ color: COLORS.textPrimary }}>
        Loading assets...
      </div>
    );
  }

  // Create rows with 3 items per row
  const rows = [];
  for (let i = 0; i < values.length; i += 3) {
    rows.push(values.slice(i, i + 3));
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut",
      },
    }),
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="assets" className={`${window.innerWidth <= 768 ? 'py-8' : 'py-16'} relative overflow-visible`}>
      {/* Glow effect background - sage green */}
      <div
        className="absolute"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.primary}22 0%, transparent 70%)`,
          filter: "blur(100px)",
          opacity: 0.4,
          zIndex: 0,
          animation: "pulse 8s infinite ease-in-out",
        }}
      />

      {title && (
        <motion.h3
          className={`${window.innerWidth <= 768 ? 'text-2xl mb-12' : 'text-3xl mb-20'} font-bold text-center`}
          style={{
            color: COLORS.textPrimary,
            backgroundImage: `linear-gradient(to right, ${COLORS.textPrimary}, ${COLORS.primary} 50%, ${COLORS.textPrimary})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            position: "relative",
            fontSize: window.innerWidth <= 768 ? "1.8rem" : "2.5rem"
          }}
          variants={titleVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {title}
          <div
            style={{
              height: "3px",
              width: "60px",
              background: COLORS.primary,
              borderRadius: "3px",
              margin: "8px auto 0"
            }}
          />
        </motion.h3>
      )}

      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={`flex flex-wrap justify-center ${window.innerWidth <= 768 ? 'gap-4 mb-8' : 'gap-8 mb-16'} relative z-10`}
        >
          {row.map((value, colIndex) => {
            const index = rowIndex * 3 + colIndex;
            return (
              <motion.div
                key={index}
                className="group relative rounded-lg overflow-visible shadow-lg transition-all duration-300 transform hover:scale-105 flex flex-col"
                style={{
                  backgroundColor: `${COLORS.primary}CC`, // ← si `COLORS.primary` est en hex, comme "#789682"
                  height: hoveredIndex === index ? "auto" : (window.innerWidth <= 768 ? "250px" : "300px"),
                  width: window.innerWidth <= 768 ? "280px" : "300px",
            zIndex: hoveredIndex === index ? 10 : 1,
                  border:
                    hoveredIndex === index
                      ? `1px solid ${COLORS.textPrimary}`
                      : "none",
                  marginBottom: window.innerWidth <= 768 ? "15px" : "20px",
                  boxShadow:
                    hoveredIndex === index
                      ? `0 0 20px ${COLORS.primary}66`
                      : "none",
          }}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
                variants={cardVariants}
                custom={index}
                initial="hidden"
                animate={isVisible ? "visible" : "hidden"}
                whileHover={{ scale: window.innerWidth <= 768 ? 1.02 : 1.05 }}
        >
            {/* Logo */}
                <motion.div
              className="icon-container mx-auto flex flex-col items-center justify-center"
              style={{
                height: 'auto',
                position: hoveredIndex === index ? 'absolute' : 'relative',
                top: hoveredIndex === index ? (window.innerWidth <= 768 ? '-15px' : '-20px') : (window.innerWidth <= 768 ? '40px' : '60px'),
                    right: hoveredIndex === index ? '0' : undefined,
                    left: hoveredIndex === index ? '0' : undefined,
                    margin: hoveredIndex === index ? '0 auto' : undefined,
                    transform: 'none',
                overflow: 'visible',
                    width: hoveredIndex === index ? (window.innerWidth <= 768 ? '35px' : '40px') : (window.innerWidth <= 768 ? '120px' : '150px'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  animate={
                    hoveredIndex === index 
                      ? { scale: [1, 0.8, 1], rotate: [0, -5, 5, 0] }
                      : { scale: 1, rotate: 0 }
                  }
                  transition={{ duration: 0.5 }}
            >
              <img
                src={value.icon}
                alt={value.title}
                className={`icon transition-all duration-300 ${hoveredIndex === index ? 'active' : ''}`}
                style={{
                  height: hoveredIndex === index ? (window.innerWidth <= 768 ? '35px' : '40px') : (window.innerWidth <= 768 ? '120px' : '150px'),
                  width: hoveredIndex === index ? (window.innerWidth <= 768 ? '35px' : '40px') : (window.innerWidth <= 768 ? '120px' : '150px'),
                      filter: 'brightness(0) saturate(100%) invert(100%)',
                      objectFit: 'contain'
                }}
              />
                </motion.div>

            {/* Titre toujours visible */}
            <div
              className="title-container mx-auto flex flex-col items-center justify-center"
              style={{
                    position: "relative",
                    top: hoveredIndex === index ? (window.innerWidth <= 768 ? '55px' : '70px') : (window.innerWidth <= 768 ? '60px' : '80px'),
                    transition: "top 0.3s ease-in-out",
              }}
            >
              <h4
                    className={`${window.innerWidth <= 768 ? 'text-lg' : 'text-xl'} font-semibold mt-4 transition-colors duration-300 text-center`}
                style={{
                      color: COLORS.creamText,
                      textShadow:
                        hoveredIndex === index
                          ? `0 0 10px ${COLORS.textPrimary}4D`
                          : "none",
                }}
              >
                {value.title}
              </h4>
            </div>

            {/* Description déroulante */}
                <div
                  className={`transition-all duration-500 overflow-hidden text-center ${window.innerWidth <= 768 ? 'px-4' : 'px-6'} ${
                    hoveredIndex === index ? "opacity-100" : "opacity-0"
                  }`}
                  style={{
                    height: hoveredIndex === index ? "auto" : "0",
                    maxHeight: hoveredIndex === index ? (window.innerWidth <= 768 ? "250px" : "300px") : "0",
                    marginTop: hoveredIndex === index ? (window.innerWidth <= 768 ? '110px' : '140px') : "0",
                    color: COLORS.creamText,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    paddingBottom: hoveredIndex === index ? (window.innerWidth <= 768 ? '15px' : '20px') : "0",
                  }}
                >
                  <p className={`mb-4 ${window.innerWidth <= 768 ? 'text-sm' : ''}`}>{value.description}</p>

                  {/* Tags */}
                  {value.tags && hoveredIndex === index && (
                    <div className="flex flex-wrap justify-center gap-2 mt-2">
                      {value.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className={`px-2 py-1 ${window.innerWidth <= 768 ? 'text-xs' : 'text-xs'} rounded-full`}
              style={{
                            backgroundColor: COLORS.creamTextWithOpacity.light,
                            border: `1px solid ${COLORS.creamTextWithOpacity.medium}`,
                            color: COLORS.creamText,
              }}
            >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
          </div>
        ))}

      <style jsx global>{`
        @keyframes pulse {
          0% {
            opacity: 0.3;
            transform: translate(-50%, -50%) scale(0.95);
          }
          50% {
            opacity: 0.4;
            transform: translate(-50%, -50%) scale(1.05);
          }
          100% {
            opacity: 0.3;
            transform: translate(-50%, -50%) scale(0.95);
          }
        }
      `}</style>
    </section>
  );
}

export default AssetsSection;